import {
  Box,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import {
  CampaignFilter,
  CampaignSegment,
  FinalCampaignSegment,
  finalCampaignSegmentSchema,
  MarketingCampaignSegmentWithFilters,
} from '../../../types/marketing';
import FullScreenFloatingModal from '../../modal/FullScreenFloatingModal';
import { FormikForm, LoadedUser } from '../../../interfaces';
import useGetSegmentCustomers from '../../../hooks/marketing/useGetSegmentCustomers';
import useCreateSegment from '../../../hooks/marketing/useCreateSegment';
import ReuseableTable from '../../table/ReuseableTable';
import { formatPhone } from '../../../utils/global/helper';
import { getFilterText } from '../../../helpers/marketing';
import TextInput from '../../form/base/TextInput';
import ColoredAlert from '../../global/ColoredAlert';
import useUser from '../../../hooks/global/useUser';

interface Props {
  segment: CampaignSegment;
  onClose: () => void;
  onCreateSuccess: (segment: MarketingCampaignSegmentWithFilters) => void;
  backButton?: boolean;
}

function CampaignSegmentReview({
  segment,
  onClose,
  onCreateSuccess,
  backButton,
}: Props) {
  const theme = useTheme();
  const isMediumOrLarger = useMediaQuery<Theme>(theme.breakpoints.up('md'));
  const { user } = useUser<LoadedUser>();

  const { data, isFetching, isError } = useGetSegmentCustomers(segment);
  const { mutateAsync: createSegment } = useCreateSegment({
    onSuccess: onCreateSuccess,
    onError: () => {
      toast.error('Failed to create segment');
    },
  });

  const modalName = isMediumOrLarger
    ? 'Build Your Own Segment'
    : 'Build A Segment';

  const form: FormikForm<FinalCampaignSegment> = {
    initialValues: {
      ...segment,
      name: '',
    },
    onSubmit: async values => {
      await createSegment(values);
    },
    validationSchema: finalCampaignSegmentSchema,
  };

  const renderCells = ({
    firstName,
    lastName,
    email,
    phoneNumber,
  }: {
    firstName: string | null | undefined;
    lastName: string | null | undefined;
    email: string | null | undefined;
    phoneNumber: string | null | undefined;
  }) => [
    <Typography variant="body2">
      {`${firstName || ''} ${lastName || ''}`.trim() || 'Unnamed'}
    </Typography>,
    <Typography variant="body2">
      {phoneNumber ? formatPhone(phoneNumber) : '-'}
    </Typography>,
    <Typography variant="body2">{email || '-'}</Typography>,
  ];

  return (
    <Formik
      enableReinitialize
      initialValues={form.initialValues}
      validationSchema={form.validationSchema}
      onSubmit={form.onSubmit}>
      {helpers => {
        const { isSubmitting, isValid, isValidating, dirty } = helpers;
        const isDisabled = isSubmitting || !isValid || isValidating || !dirty;

        return (
          <Form
            style={{
              width: '100%',
              padding: 0,
              boxSizing: 'border-box',
            }}>
            <FullScreenFloatingModal
              isOpen={!!segment}
              name={modalName}
              onClose={onClose}
              submitButton={{
                text: 'Save',
                disabled: isDisabled || isError,
                onClick: () => helpers.submitForm(),
                type: 'submit',
                isLoading: isSubmitting,
              }}
              backButton={backButton}>
              {isError && !isFetching && (
                <ColoredAlert
                  type="error"
                  animate={false}
                  dismissible={false}
                  title="Failed to fetch the contacts for your selected filters"
                  subtitle="Please return to the previous step and try again"
                />
              )}

              <Stack
                gap={3}
                divider={
                  <Divider
                    sx={{
                      borderColor: theme.palette.lightGrey.main,
                    }}
                  />
                }>
                <Typography variant="h2" color={theme.palette.darkGrey.main}>
                  Review your segment
                </Typography>

                <TextInput
                  name="name"
                  disabled={isSubmitting}
                  placeholder="Enter a name for your segment"
                />

                <Box display="flex" alignItems="center" gap={1.25}>
                  {!isFetching && (
                    <Typography
                      variant="h1"
                      component="p"
                      color="#000000"
                      fontWeight="700">
                      {data?.totalCount || 0}
                    </Typography>
                  )}
                  {isFetching && (
                    <CircularProgress
                      size={theme.typography.h5.fontSize}
                      color="primary"
                    />
                  )}
                  <Stack>
                    <Typography variant="h5" component="p" fontWeight="700">
                      subscribers in your segment
                    </Typography>
                    {!isFetching && (
                      <Typography
                        variant="body2"
                        component="p"
                        color={theme.palette.medGrey.main}>
                        as of{' '}
                        {data?.date && !isFetching
                          ? format(data?.date, "MM/dd/yy 'at' h:mma")
                          : '-'}
                      </Typography>
                    )}
                    {isFetching && <Skeleton variant="text" width={150} />}
                  </Stack>
                </Box>

                <Stack
                  width="100%"
                  divider={
                    <Typography
                      variant="h5"
                      component="p"
                      my={2.5}
                      color={theme.palette.darkGrey.main}
                      fontWeight="700">
                      {segment.condition === 'ALL' ? 'And' : 'Or'}
                    </Typography>
                  }>
                  {segment.filters.map((f, index) => {
                    const filter = f as CampaignFilter;
                    return (
                      <Typography
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        variant="body1"
                        color={theme.palette.darkGrey.main}>
                        {getFilterText({
                          filter,
                          employees: user?.business?.employees,
                        })}
                      </Typography>
                    );
                  })}
                </Stack>

                <Stack gap={2.5}>
                  <Typography variant="h5" component="p" fontWeight="700">
                    Sample contacts in your segment
                  </Typography>

                  <ColoredAlert
                    type="info"
                    subtitle="To ensure each person receives only one message, contacts with the same email address or phone number will only be counted once."
                    animate={false}
                    persistentDismissAlertKey="segment-review-duplicate-contacts"
                  />

                  <ReuseableTable
                    small
                    name="Customers"
                    emptyMessage="No contacts found"
                    headerCells={[
                      { id: 'name', label: 'Name' },
                      { id: 'phone', label: 'Phone Number' },
                      { id: 'email', label: 'Email' },
                    ]}
                    loading={isFetching}
                    rows={[
                      ...(data?.sourceCustomers ?? []).map(customer => ({
                        rowId: customer.id,
                        cells: renderCells({
                          firstName: customer.metadata?.first_name,
                          lastName: customer.metadata?.last_name,
                          email: customer.email_address,
                          phoneNumber: customer.phone_number,
                        }),
                      })),
                      ...(data?.customers ?? []).map(customer => ({
                        rowId: customer.customerId,
                        cells: renderCells({
                          firstName: customer.user?.firstName,
                          lastName: customer.user?.lastName,
                          email: customer.user.email,
                          phoneNumber: customer.user.phoneNumber,
                        }),
                      })),
                    ]}
                  />
                </Stack>
              </Stack>
            </FullScreenFloatingModal>
          </Form>
        );
      }}
    </Formik>
  );
}

export default CampaignSegmentReview;
